import { saveFiltersAPI } from '../Dashboard/filtersAPI';
import { fetchProjectsAPI, getQuickSummaryStatuses } from '../Dashboard/dashboardAPI';

const useHandlers = ({
  cmoSite,
  cmoOrgId,
  deliverableType,
  searchCriteria,
  searchValue,
  currentPage,
  // UI-PD-Changes: current tile selection, needed to toggle a tile off
  // when the same tile is clicked a second time.
  quickSummaryFilter,
  setCmoSite,
  setCmoOrgId,
  setCmo,
  setSite,
  setDeliverableType,
  setInitialCmoSite,
  setInitialCmoOrgId,
  setInitialDeliverableType,
  setQuickSummaryFilter,
  setSearchCriteria,
  setSearchValue,
  setCurrentPage,
  setProjects,
  setSelectedBatchNumbers,
  setSelectedUnitProcedures,
  setLastSyncTime,
  setQuickSummaryStatuses,
  setLoading,
  setFiltersLoading,
  setSnackbar,
  setFilterDialog,
  loadDashboardData,
}) => {
  // Handle save filters
  // Persists to the shared saved-filters endpoint (POST /user/saveFilters via
  // saveFiltersAPI) and reports the outcome in a dialog, matching how the
  // Purchase Order dashboard confirms a save.
  const handleSaveFilters = async () => {
    try {
      setFiltersLoading(true);

      const filterData = {
        cmoSite: cmoSite || 'all',
        cmoOrgId: cmoOrgId || 'all',
        deliverableType: deliverableType || 'all',
      };

      const result = await saveFiltersAPI(filterData);

      if (result?.status) {
        // Update initial values to current values
        setInitialCmoSite(cmoSite);
        if (setInitialCmoOrgId) setInitialCmoOrgId(cmoOrgId);
        setInitialDeliverableType(deliverableType);

        setFilterDialog({
          open: true,
          success: true,
          title: 'Saved successfully',
        });
      } else {
        setFilterDialog({
          open: true,
          success: false,
          title: 'Failed to save filters',
        });
      }
    } catch (error) {
      console.error('Error saving filters:', error);
      setFilterDialog({
        open: true,
        success: false,
        title: 'Failed to save filters',
      });
    } finally {
      setFiltersLoading(false);
    }
  };

  // Handle clear filters
  // Clears the selection and refetches the dashboard - it deliberately does
  // NOT persist anything and shows no popup, matching the Purchase Order
  // dashboard: clearing is a view-level action, and the cleared selection
  // only becomes the saved state once the user presses Save filters. Because
  // the initial* baselines are left untouched, Save filters becomes enabled
  // straight after a clear.
  const handleClearFilters = async () => {
    try {
      setFiltersLoading(true);

      const clearedFilters = {
        cmoSite: 'all',
        cmoOrgId: 'all',
        deliverableType: 'all',
      };

      // Reset all filter states to defaults
      setCmoSite('all');
      if (setCmoOrgId) setCmoOrgId('all');
      if (setCmo) setCmo('all');
      if (setSite) setSite('all');
      setDeliverableType('all');
      setQuickSummaryFilter(null);
      setSearchCriteria('all');
      setSearchValue('');
      setCurrentPage(1);

      // Reload data with cleared filters (all defaults)
      await loadDashboardData({
        ...clearedFilters,
        projectStatusID: null,
        searchCriteria: 'all',
        searchValue: '',
        pageNumber: 1,
      });

      // Fetch quick summary with cleared filters
      const quickSummaryResponse = await getQuickSummaryStatuses(clearedFilters);

      if (quickSummaryResponse?.quickSummaryStatuses) {
        setQuickSummaryStatuses(quickSummaryResponse.quickSummaryStatuses);
      }

      // No confirmation popup on clear - the cleared dashboard is the feedback
      // (same as the Purchase Order dashboard, which shows nothing on clear).
    } catch (error) {
      console.error('Error clearing filters:', error);
    } finally {
      setFiltersLoading(false);
    }
  };

  // Handle quick summary status click
  // UI-PD-Changes: clicking the ALREADY-selected tile toggles the filter
  // OFF (second click clears it); clicking a different tile applies it.
  const handleQuickStatus = async (statusIDs) => {
    try {
      setLoading(true);

      const isTogglingOff = quickSummaryFilter === statusIDs;
      const nextFilter = isTogglingOff ? null : statusIDs;
      setQuickSummaryFilter(nextFilter);

      // Reload data with (or without) the quick summary filter
      await loadDashboardData({
        cmoSite,
        cmoOrgId,
        deliverableType,
        projectStatusID: nextFilter,
        searchCriteria,
        searchValue,
        pageNumber: 1,
      });
    } catch (error) {
      console.error('Error filtering by quick status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to filter projects',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle clear quick summary filter
  const handleClearQuickSummary = async () => {
    try {
      setLoading(true);
      setQuickSummaryFilter(null);

      // Reload data without quick summary filter
      await loadDashboardData({
        cmoSite,
        cmoOrgId,
        deliverableType,
        projectStatusID: null,
        searchCriteria,
        searchValue,
        pageNumber: 1,
      });
    } catch (error) {
      console.error('Error clearing quick summary filter:', error);
      setSnackbar({
        open: true,
        message: 'Failed to clear quick summary filter',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle clear search — cross icon next to the Select Criteria field.
  // Removes both the criteria (key) and the value, then refreshes the list
  // (mirrors the PO dashboard's handleClearSearch behaviour).
  const handleClearSearch = async () => {
    try {
      setLoading(true);

      // Reset criteria + value (key & value both cleared)
      setSearchCriteria('all');
      setSearchValue('');
      setQuickSummaryFilter(null);
      setCurrentPage(1);

      // Refresh with search cleared (criteria "all" neutralises the value).
      await loadDashboardData({
        cmoSite,
        cmoOrgId,
        deliverableType,
        projectStatusID: null,
        searchCriteria: 'all',
        searchValue: '',
        pageNumber: 1,
      });
    } catch (error) {
      console.error('Error clearing search:', error);
      setSnackbar({
        open: true,
        message: 'Failed to clear search',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async () => {
    try {
      setLoading(true);

      // A new search produces a new result set — always start from page 1 so the
      // pager cannot stay on a page the search results no longer have.
      setCurrentPage(1);

      // Reload data with search criteria
      await loadDashboardData({
        cmoSite,
        cmoOrgId,
        deliverableType,
        projectStatusID: null, // Clear quick summary filter when searching
        searchCriteria,
        searchValue,
        pageNumber: 1,
      });

      // Clear quick summary filter when searching
      setQuickSummaryFilter(null);
    } catch (error) {
      console.error('Error searching:', error);
      setSnackbar({
        open: true,
        message: 'Failed to search projects',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Handle close of the save/clear filters dialog
  const handleCloseFilterDialog = () => {
    setFilterDialog((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return {
    handleSaveFilters,
    handleClearFilters,
    handleQuickStatus,
    handleClearQuickSummary,
    handleSearch,
    handleClearSearch,
    handleCloseSnackbar,
    handleCloseFilterDialog,
  };
};

export { useHandlers };
export default useHandlers;

